import { z } from 'zod';
import {
  BaseResponseSchema,
  emailSchema,
  integerIdSchema,
  uuidSchema,
} from './primitives.js';

// Specialized API Schemas (for routes that need them)
// These can be expanded as needed
export const InvoiceRequestSchema = z.object({
  invoiceId: uuidSchema
});

export const PaymentMethodIdSchema = z.object({
  paymentMethodId: uuidSchema,
});

export const CustomerIdParamSchema = z.object({
  customerId: integerIdSchema,
});

export const StripCustomerIdParamSchema = z.object({
  customerId: z.string(), // Can be empty string for new customers
});

// Schema for payment method types used in payment intents (comprehensive list)
export const PaymentTypesSchema = z.object({
  type: z.union([
    z.enum(['acss_debit', 'affirm', 'afterpay_clearpay', 'alipay', 'alma', 'amazon_pay', 'au_becs_debit', 'bacs_debit', 'bancontact', 'billie', 'blik', 'boleto',
      'card', 'cashapp', 'crypto', 'customer_balance', 'eps', 'fpx', 'giropay', 'grabpay', 'ideal', 'kakao_pay', 'klarna', 'konbini', 'kr_card', 'link', 'mb_way', 'mobilepay',
      'multibanco', 'naver_pay', 'nz_bank_account', 'oxxo', 'p24', 'pay_by_bank', 'payco', 'paynow', 'paypal', 'pix', 'promptpay', 'revolut_pay', 'samsung_pay', 'satispay',
      'sepa_debit', 'sofort', 'swish', 'twint', 'us_bank_account', 'wechat_pay', 'zip']),
    z.array(z.enum(['acss_debit', 'affirm', 'afterpay_clearpay', 'alipay', 'alma', 'amazon_pay', 'au_becs_debit', 'bacs_debit', 'bancontact', 'billie', 'blik', 'boleto',
      'card', 'cashapp', 'crypto', 'customer_balance', 'eps', 'fpx', 'giropay', 'grabpay', 'ideal', 'kakao_pay', 'klarna', 'konbini', 'kr_card', 'link', 'mb_way', 'mobilepay',
      'multibanco', 'naver_pay', 'nz_bank_account', 'oxxo', 'p24', 'pay_by_bank', 'payco', 'paynow', 'paypal', 'pix', 'promptpay', 'revolut_pay', 'samsung_pay', 'satispay',
      'sepa_debit', 'sofort', 'swish', 'twint', 'us_bank_account', 'wechat_pay', 'zip']))
  ]),
});

// Schema for payment method types supported by listPaymentMethods API
export const ListPaymentMethodTypesSchema = z.object({
  type: z.union([
    z.enum(['card', 'sepa_debit', 'au_becs_debit', 'bacs_debit', 'us_bank_account', 'acss_debit', 'link']),
    z.array(z.enum(['card', 'sepa_debit', 'au_becs_debit', 'bacs_debit', 'us_bank_account', 'acss_debit', 'link']))
  ]).optional(),
});

export const StripeCustomerIdPaymentTypeAndUsageSchema = StripCustomerIdParamSchema.merge(PaymentTypesSchema).merge(
  z.object({ usage: z.enum(['on_session', 'off_session']) })
)

export const PaymentMethodAndStripeCustomerIdSchema = PaymentMethodIdSchema.merge(StripCustomerIdParamSchema);

// Schema for attaching payment method - allows empty customerId for new customers
export const AttachPaymentMethodRequestSchema = PaymentMethodIdSchema.merge(
  z.object({
    customerId: z.string(), // Can be empty string for new customers
    setAsDefault: z.boolean().optional(),
  })
);

export const PaymentCardSchema = z.object({
  cardNumber: z.string(),
  expMonth: z.number().int().min(1).max(12),
  expYear: z.number().int().min(new Date().getFullYear()),
  cvc: z.string().min(3).max(4),
})

export const DefaultPaymentMethodResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    customerId: z.string(), // Stripe customer ID
    email: emailSchema.nullable(),
    defaultPaymentMethod: z.string().nullable().optional()
  })
})

export const PaymentCardValidationResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    isValid: z.boolean(),
    cardType: z.string().min(1),
    isValidLuhn: z.boolean(),
    isExpired: z.boolean(),
    errors: z.array(z.string()),
  })
})

export const SubscriptionIdSchema = z.object({
  subscriptionId: uuidSchema
});

export const SubscriptionPlanIdSchema = z.object({
  planId: uuidSchema
});

export const SubscriptionRequestSchema = z.object({
  subscriptionId: uuidSchema
});

export const SubscriptionCreateRequestSchema = z.object({
  customerId: z.string(), // Stripe customer ID (e.g., cus_xxxxx)
  planId: z.string(), // Stripe price ID (e.g., price_xxxxx)
  trialDays: z.number().int().min(0).max(365).optional(),
  couponId: z.string().optional(),
  metadata: z.any().optional()
});

export const SubscriptionUpdateRequestSchema = z.object({
  planId: uuidSchema,
  metadata: z.any().optional(),
  prorationBehavior: z.enum(['create_prorations', 'none', 'always_invoice']).optional()
});

export const SubscriptionCancelRequestSchema = z.object({
  immediately: z.boolean(),
  reason: z.string().min(1).max(500).optional(),
});

export const SubscriptionCalculateProrationForNewPlanRequestSchema = z.object({
  newPlanId: uuidSchema
});

// Note: Old TenantSubscription schemas removed - now using unified Subscription model
// Inferred types
export type AttachPaymentMethodRequest = z.infer<typeof AttachPaymentMethodRequestSchema>;
export type CustomerIdParam = z.infer<typeof CustomerIdParamSchema>;
export type DefaultPaymentMethodResponse = z.infer<typeof DefaultPaymentMethodResponseSchema>;
export type InvoiceRequest = z.infer<typeof InvoiceRequestSchema>;
export type ListPaymentMethodTypes = z.infer<typeof ListPaymentMethodTypesSchema>;
export type PaymentCard = z.infer<typeof PaymentCardSchema>;
export type PaymentCardValidationResponse = z.infer<typeof PaymentCardValidationResponseSchema>;
export type PaymentMethodAndStripeCustomerId = z.infer<typeof PaymentMethodAndStripeCustomerIdSchema>;
export type PaymentMethodId = z.infer<typeof PaymentMethodIdSchema>;
export type PaymentTypes = z.infer<typeof PaymentTypesSchema>;
export type StripCustomerIdParam = z.infer<typeof StripCustomerIdParamSchema>;
export type StripeCustomerIdPaymentTypeAndUsage = z.infer<typeof StripeCustomerIdPaymentTypeAndUsageSchema>;
export type SubscriptionCalculateProrationForNewPlanRequest = z.infer<typeof SubscriptionCalculateProrationForNewPlanRequestSchema>;
export type SubscriptionCancelRequest = z.infer<typeof SubscriptionCancelRequestSchema>;
export type SubscriptionCreateRequest = z.infer<typeof SubscriptionCreateRequestSchema>;
export type SubscriptionId = z.infer<typeof SubscriptionIdSchema>;
export type SubscriptionPlanId = z.infer<typeof SubscriptionPlanIdSchema>;
export type SubscriptionRequest = z.infer<typeof SubscriptionRequestSchema>;
export type SubscriptionUpdateRequest = z.infer<typeof SubscriptionUpdateRequestSchema>;
